/**
 * Database - Admin Dashboard
 * Real data only: health + metrics + backup list.
 */

import { useAuthContext } from "@/contexts/AuthContext";
import { AdminLayout } from "../../layout/AdminLayout";
import { EnterprisePageHeader } from "../../components/layout/EnterprisePageHeader";
import { useDatabaseManagement } from "@/hooks/useDatabaseManagement";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
    Activity,
    AlertCircle,
    BarChart3,
    Database,
    HardDrive,
    MemoryStick,
    Users,
    Zap,
} from "lucide-react";

function HealthBadge({ status }: { status: "healthy" | "down" }) {
    if (status === "healthy") {
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Healthy</Badge>;
    }
    return <Badge variant="destructive">Down</Badge>;
}

function formatSeconds(sec: number) {
    if (!Number.isFinite(sec) || sec <= 0) return "-";
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

export default function DatabaseManagementPage() {
    const { user, logout } = useAuthContext();
    const { health, metrics, backups, isLoading, error, refresh } = useDatabaseManagement();

    const handleLogout = () => {
        void logout();
    };

    const pg = health.find((h) => h.service === "postgresql");
    const rd = health.find((h) => h.service === "redis");

    const pgConnPct =
        pg?.connections && pg.connections.max > 0
            ? (pg.connections.active / pg.connections.max) * 100
            : 0;

    const redisMemPct =
        rd?.memory?.max_mb && rd.memory.max_mb > 0
            ? (rd.memory.used_mb / rd.memory.max_mb) * 100
            : 0;

    return (
        <AdminLayout user={user} onLogout={handleLogout}>
            <div className="space-y-6 sm:space-y-8">
                <EnterprisePageHeader
                    badge="Database"
                    title="Database Control"
                    subtitle="PostgreSQL + Redis health, metrics e backup operativi"
                    icon={<Database className="h-6 w-6" />}
                    isLoading={isLoading}
                    onRefresh={refresh}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Database className="h-4 w-4 text-blue-500" />
                                    PostgreSQL
                                </div>
                                {pg && <HealthBadge status={pg.status} />}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm">Response Time</span>
                                <span className="font-mono text-sm">{pg?.response_time_ms ?? 0}ms</span>
                            </div>
                            {pg?.connections && (
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span>Connections</span>
                                        <span>
                                            {pg.connections.active}/{pg.connections.max} (idle {pg.connections.idle})
                                        </span>
                                    </div>
                                    <Progress value={pgConnPct} className="h-2" />
                                </div>
                            )}
                            {pg?.error && (
                                <div className="text-xs text-red-500 break-all">{pg.error}</div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <MemoryStick className="h-4 w-4 text-red-500" />
                                    Redis
                                </div>
                                {rd && <HealthBadge status={rd.status} />}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm">Response Time</span>
                                <span className="font-mono text-sm">{rd?.response_time_ms ?? 0}ms</span>
                            </div>
                            {rd?.memory && (
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span>Memory</span>
                                        <span>
                                            {rd.memory.used_mb.toFixed(1)} MB
                                            {rd.memory.max_mb ? ` / ${rd.memory.max_mb.toFixed(1)} MB` : ""}
                                        </span>
                                    </div>
                                    {rd.memory.max_mb ? (
                                        <Progress value={redisMemPct} className="h-2" />
                                    ) : (
                                        <div className="text-xs text-muted-foreground">maxmemory not configured</div>
                                    )}
                                </div>
                            )}
                            {rd?.error && (
                                <div className="text-xs text-red-500 break-all">{rd.error}</div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-primary" />
                                Metrics
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="postgresql" className="w-full">
                                <TabsList className="grid h-auto w-full grid-cols-2 gap-1">
                                    <TabsTrigger value="postgresql" className="text-xs sm:text-sm">PostgreSQL</TabsTrigger>
                                    <TabsTrigger value="redis" className="text-xs sm:text-sm">Redis</TabsTrigger>
                                </TabsList>

                                <TabsContent value="postgresql" className="pt-4 space-y-4">
                                    {metrics?.postgresql ? (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Users className="h-4 w-4 text-blue-500" />
                                                    <span className="text-sm font-medium">Connections</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.postgresql.connections.active}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    idle {metrics.postgresql.connections.idle} / max {metrics.postgresql.connections.max}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="h-4 w-4 text-green-500" />
                                                    <span className="text-sm font-medium">Transactions</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.postgresql.queries.total.toLocaleString()}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    commits/sec {metrics.postgresql.performance.commits_per_sec.toFixed(2)}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <HardDrive className="h-4 w-4 text-orange-500" />
                                                    <span className="text-sm font-medium">DB Size</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.postgresql.size.database_size_gb.toFixed(2)} GB</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {metrics.postgresql.size.table_count} tables, {metrics.postgresql.size.index_count} indexes
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Activity className="h-4 w-4 text-purple-500" />
                                                    <span className="text-sm font-medium">Cache Hit</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.postgresql.performance.cache_hit_ratio.toFixed(1)}%</div>
                                                <div className="text-xs text-muted-foreground">
                                                    uptime {formatSeconds(metrics.postgresql.uptime_seconds)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-muted-foreground">No metrics available</div>
                                    )}
                                </TabsContent>

                                <TabsContent value="redis" className="pt-4 space-y-4">
                                    {metrics?.redis ? (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <MemoryStick className="h-4 w-4 text-red-500" />
                                                    <span className="text-sm font-medium">Memory</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.redis.memory.used_mb.toFixed(1)} MB</div>
                                                <div className="text-xs text-muted-foreground">
                                                    max {metrics.redis.memory.max_mb.toFixed(1)} MB, frag {metrics.redis.memory.fragmentation_ratio.toFixed(2)}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="h-4 w-4 text-green-500" />
                                                    <span className="text-sm font-medium">Ops/sec</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.redis.performance.ops_per_sec.toFixed(0)}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    hit ratio {metrics.redis.performance.hit_ratio.toFixed(1)}%
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Database className="h-4 w-4 text-blue-500" />
                                                    <span className="text-sm font-medium">Keys</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.redis.keys.total.toLocaleString()}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    expired {metrics.redis.keys.expired}, evicted {metrics.redis.keys.evicted}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Users className="h-4 w-4 text-purple-500" />
                                                    <span className="text-sm font-medium">Clients</span>
                                                </div>
                                                <div className="text-xl font-bold">{metrics.redis.clients.connected}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    blocked {metrics.redis.clients.blocked}, uptime {formatSeconds(metrics.redis.uptime_seconds)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-muted-foreground">No metrics available</div>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <HardDrive className="h-5 w-5 text-primary" />
                                Backups
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-72 sm:h-96">
                                {backups.length > 0 ? (
                                    <div className="space-y-2">
                                        {backups.map((b) => (
                                            <div key={b.id} className="p-3 border rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <div className="font-medium text-sm">{b.filename}</div>
                                                    <Badge variant="outline" className="text-xs">
                                                        {b.type}
                                                    </Badge>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {b.created_at} · {b.status}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted-foreground py-8">
                                        <HardDrive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p>No backups available</p>
                                    </div>
                                )}
                            </ScrollArea>
                            <div className="text-xs text-muted-foreground mt-3">
                                Backups are produced by ops services; the API exposes a read-only list.
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {error && (
                    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <span className="font-medium">Connection error</span>
                            </div>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
}
